import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Truck, ArrowRight, CheckCircle2, TrendingUp, Wallet, Calculator, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";

const PAGE_URL = "https://routehaulpathways.com/how-much-do-owner-operators-make";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/513dd16005890ba7ec304191a7f67743f43e1aba091f3c2b9aa07656ce546668.jpeg";
const TITLE = "How Much Do Owner-Operators Make? (2026 Gross vs. Net Pay) | RouteHaul";
const DESCRIPTION =
  "How much do owner-operators make in 2026? Realistic gross revenue ($150k–$350k) vs. net take-home pay ($35k–$120k+), what drives the difference, and how to know your real numbers before you go all-in.";

const INCOME_TIERS = [
  { label: "Gross annual revenue (before expenses)", value: "$150,000 – $350,000", note: "What the truck bills — not what you keep." },
  { label: "First 1–2 years, net take-home", value: "$35,000 – $55,000", note: "Startup costs, thin lanes and learning the numbers." },
  { label: "Well-run operation, net", value: "$60,000 – $120,000", note: "Solid cost control and lane selection." },
  { label: "Top performers, net", value: "$150,000+", note: "Direct shippers, low deadhead, tight margins." },
];

const DRIVERS = [
  { icon: Calculator, title: "Cost per mile (CPM)", text: "The single biggest lever. Two trucks earning the same revenue can take home wildly different pay based on their all-in cost per mile — commonly around $2.00–$2.30/mi. If you don't know yours, you're guessing." },
  { icon: TrendingUp, title: "How you price loads", text: "Take-home comes from the gap between your rate per mile and your cost per mile. Operators who price above break-even with a real margin keep money; those who chase cheap freight to 'stay busy' burn out broke." },
  { icon: Truck, title: "Deadhead & empty miles", text: "Every unpaid mile dilutes your rate. Planning backhauls and dense lanes protects your revenue per mile more than raw miles ever will." },
  { icon: Wallet, title: "Fixed costs & cash flow", text: "Truck payment, insurance (often $15k–$35k/yr for a new authority) and slow broker pay (30–45 days) all eat net pay. Factoring and a cash reserve keep you solvent while you build." },
];

const FAQS = [
  { q: "How much do owner-operators make per year?", a: "Gross revenue typically lands between $150,000 and $350,000 a year, but net take-home is what matters — usually $60,000–$120,000 for a well-run operation. New entrants in their first year or two often net closer to $35,000–$55,000 while they absorb startup costs and learn to price freight." },
  { q: "What's the difference between gross and net for an owner-operator?", a: "Gross is the total the truck bills before any expenses. Net is what's left after fuel, insurance, truck payment, maintenance, tolls, permits, factoring fees and taxes. Fuel alone can run $50,000–$85,000 a year, so gross revenue on its own tells you almost nothing about pay." },
  { q: "Why do two owner-operators with the same revenue take home different amounts?", a: "Cost per mile. One operator might run at $1.80/mi all-in while another runs at $2.30/mi. On the same miles and revenue, the lower-cost operator keeps far more. That's why knowing and controlling your cost per mile is step one." },
  { q: "How much do owner-operators make per mile?", a: "Owner-operators commonly gross around $2.00–$3.00+ per mile depending on equipment, lanes and the market, while all-in operating cost is often $2.00–$2.30 per mile. Your profit is the spread between the two — which is exactly why you price every load against your own break-even, not a rule of thumb." },
  { q: "Do owner-operators make more than company drivers?", a: "They can — but not automatically. Owner-operators keep more of each load but also carry every cost and risk. A disciplined owner-operator who controls costs and prices well usually out-earns a company driver; one who doesn't know their numbers can easily make less while working far harder." },
  { q: "How can I estimate my own take-home pay?", a: "Start with your cost per mile, then subtract it from your expected rate per mile across your monthly miles. RouteHaul's free cost-per-mile calculator does the math instantly, and the free guide walks you through pricing loads above break-even." },
];

export default function OwnerOperatorSalary() {
  const { user } = useAuth();
  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "How much do owner-operators make — RouteHaul Logistics Pathways",
    seoKey: "salary",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: "How Much Do Owner-Operators Make? (2026 Gross vs. Net Pay)",
          description: DESCRIPTION,
          about: "Owner-operator trucking income and take-home pay",
          author: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          mainEntityOfPage: PAGE_URL,
          image: OG_IMAGE,
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  }), []);

  return (
    <div className="min-h-screen bg-white" data-testid="salary-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="salary-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="salary-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="salary-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="salary-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-5">
              <span className="w-8 h-px bg-brand" /> Owner-Operator Pay
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[1.02]">
              How much do owner-operators make?
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-700">
              The honest 2026 answer: the truck might <em>gross</em> $150,000–$350,000, but what you
              actually take home is a different number — and it comes down almost entirely to your costs
              and how you price loads.
            </p>
          </motion.div>

          {/* Quick answer box (snippet bait) */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="salary-quick-answer">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Quick answer
            </div>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              A well-run owner-operator typically nets <strong>$60,000–$120,000</strong> a year after all
              expenses, on gross revenue of <strong>$150,000–$350,000</strong>. New operators often net
              <strong> $35,000–$55,000</strong> in their first year or two. The number that decides your pay
              isn't gross revenue — it's your <strong>cost per mile</strong> and how far above it you price
              your freight.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/cost-per-mile-calculator" data-testid="salary-hero-cta">
              <Button className="h-13 px-7 py-3 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group w-full sm:w-auto">
                Calculate your cost per mile free
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Income breakdown */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-3">Gross vs. net: what the numbers really look like</h2>
        <p className="text-slate-600 mb-10 max-w-2xl">Revenue is what the truck bills. Take-home is what lands in your account after fuel, insurance, payments, maintenance and taxes. Here's the realistic 2026 range.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {INCOME_TIERS.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.06 }}
              data-testid={`salary-tier-${i}`}
              className="rounded-2xl border border-slate-200 p-7"
            >
              <div className="font-mono font-black text-2xl sm:text-3xl text-brand tracking-tight">{t.value}</div>
              <div className="mt-2 font-heading font-bold text-slate-900 text-sm">{t.label}</div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t.note}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What drives take-home */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black mb-3">What actually drives your take-home pay</h2>
          <p className="text-slate-300 mb-10 max-w-2xl">Four levers decide whether a busy truck is a profitable one:</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {DRIVERS.map((d, i) => (
              <div key={d.title} data-testid={`salary-driver-${i}`} className="rounded-2xl border border-white/10 bg-white/5 p-7 flex gap-5">
                <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center shrink-0"><d.icon className="w-6 h-6 text-brand" strokeWidth={2.2} /></div>
                <div>
                  <h3 className="font-heading text-lg font-bold mb-1.5">{d.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{d.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reality check */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-7">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-600" /><h2 className="font-heading text-xl font-black text-slate-900">The mistake that quietly caps your pay</h2></div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Most owner-operators who struggle aren't bad drivers — they simply don't know their true cost per
            mile, so they accept loads that feel fine but actually lose money after expenses. Chasing gross
            revenue and staying "busy" on cheap freight is how profitable-looking trucks go broke. Know your
            number first, then only book freight that clears it with margin to spare.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Find out what you'd actually keep</h2>
          <p className="mt-4 text-orange-50 text-lg">Use the free cost-per-mile calculator to find your break-even, then create a free account for the 12-Step guide that teaches you to price every load for profit.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/cost-per-mile-calculator" data-testid="salary-cpm-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Free cost-per-mile calculator
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/register" data-testid="salary-final-cta">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                Start the free guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 text-center mb-10">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`sfaq-${i}`} data-testid={`salary-faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-heading font-bold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not legal, tax, or financial advice. Income figures are general ranges, not a promise of earnings.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-much-does-it-cost-to-start-a-trucking-company" className="hover:text-slate-900">Startup Costs</Link>
            <Link to="/owner-operator-taxes" className="hover:text-slate-900">Taxes</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/ifta-fuel-tax-calculator" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/how-to-get-your-own-authority" className="hover:text-slate-900">Get Your Authority</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
